import { inject, Injectable } from '@angular/core';
import * as braintreeClient from 'braintree-web/client';
/**
 * PayPal Checkout V6 shipped in braintree-web 3.135.0, but @types/braintree-web (3.96.17, the latest
 * published) has no declaration for it and the module carries no types of its own, so this import is
 * an implicit `any` (TS7016). No type is lost by suppressing it: `create` resolves to `any` either
 * way, and its result is cast to PayPalCheckoutV6Instance below, which is where the real typing lives.
 *
 * A static import is what matters here — the previous `require()` call had no ESM equivalent in the
 * built library and threw "require is not defined" in the browser.
 */
// @ts-ignore -- no published typings for this entry point; see comment above.
import * as braintreePaypalCheckoutV6 from 'braintree-web/paypal-checkout-v6';
import { ProviderName } from '../../../../account/models/login-response.model';
import { LoggingService } from '../../core/logging.service';
import { ExpressCheckoutService } from './express-checkout.service';
import { ExpressCheckoutCallback } from '../../../models/express-checkout/express-checkout-endpoints.model';
import { ExpressCheckoutContext } from '../../../models/orders/express-checkout.token';

/**
 * Minimal typings for the braintree-web/paypal-checkout-v6 module — @types/braintree-web does not
 * ship a declaration for V6 yet. Only the members this service uses are described.
 */
interface PayPalV6TokenizePayload {
  nonce: string;
}
interface PayPalV6Session {
  /** Launches the PayPal flow. Must be called from a user gesture (e.g. a click handler). */
  start(presentationOptions?: { presentationMode?: string }): Promise<unknown>;
}
interface PayPalV6ApprovalCallbacks {
  onApprove: (data: unknown) => unknown;
  onCancel?: (data: unknown) => void;
  onError?: (error: unknown) => void;
}
/** One-time payment session options (checkout flow — pays an amount now). */
interface PayPalV6PaymentSessionOptions extends PayPalV6ApprovalCallbacks {
  amount: string;
  currency: string;
  intent?: string;
}
/** Billing agreement session options (vault flow — saves a payment method, no amount). */
type PayPalV6BillingAgreementSessionOptions = PayPalV6ApprovalCallbacks;
interface PayPalCheckoutV6Instance {
  loadPayPalSDK(options?: { env?: string }): Promise<PayPalCheckoutV6Instance>;
  createOneTimePaymentSession(options: PayPalV6PaymentSessionOptions): PayPalV6Session;
  createBillingAgreementSession(options: PayPalV6BillingAgreementSessionOptions): PayPalV6Session;
  tokenizePayment(data: unknown): Promise<PayPalV6TokenizePayload>;
  teardown(): Promise<void>;
}

/** Callbacks the component wires to the PayPal V6 session lifecycle. */
export interface BraintreePaypalSessionOptions {
  /** Called with the payment nonce once the buyer authorises. */
  onAuthorised: (nonce: string) => void;
  /** Called when the buyer cancels the PayPal flow. */
  onCancelled?: (data: unknown) => void;
  /** Called on any PayPal / Braintree error. */
  onError?: (error: unknown) => void;
}

/**
 * Braintree PayPal express checkout provider service (PayPal Web SDK v6). Creates a Braintree client
 * from a client token, creates a PayPal Checkout V6 instance, and loads the PayPal SDK. On buyer
 * authorisation it tokenizes the payment into a nonce that is posted to the backend
 * (api/checkout/express/complete or api/cardtokens/express/complete via ExpressCheckoutService).
 *
 * V6 has no `paypal.Buttons(...).render()`: instead a session is created and its `start()` is called
 * from the user's click. The flow is chosen by `context.mode`:
 *   - 'card-token'  → vault flow    → createBillingAgreementSession
 *   - 'marketplace' → checkout flow → createOneTimePaymentSession
 *
 * The client token is supplied via the component-provided settings (EXPRESS_CHECKOUT_SETTINGS) as
 * `authorization`, and passed straight to braintreeClient.create.
 */
@Injectable({
  providedIn: 'root',
})
export class BraintreeService {
  private logService = inject(LoggingService);
  private expressCheckoutService = inject(ExpressCheckoutService);

  /**
   * The client token the current `paypalCheckout` was built from, or null when nothing is set up.
   * Keyed on the token rather than a plain boolean: this service is a root singleton, so a later
   * checkout in the same session (a different mode, or a different member after re-login) arrives
   * with a fresh token, and a boolean latch would keep driving PayPal with the expired first one.
   */
  private initializedFor: string | null = null;
  private paypalCheckout: PayPalCheckoutV6Instance | null = null;

  /* ================================
   * Public API
   * ================================ */

  /**
   * Create the Braintree client and PayPal Checkout V6 instance, and load the PayPal SDK. Returns
   * false (without throwing) so the container can fall back to the native flow. V6 requires a client
   * token (tokenization keys are unsupported); the environment is derived from the token, not set here.
   */
  async initialize(options: { settings?: Record<string, any> }): Promise<boolean> {
    const authorization: string | undefined = options.settings?.authorization;
    if (!authorization) {
      this.logService.info('Braintree: no client token available, skipping initialize');
      return false;
    }

    // Already set up for this exact token — reuse it rather than building a second PayPal instance.
    if (this.paypalCheckout && this.initializedFor === authorization) {
      return true;
    }

    // A different token means the existing instance is stale; drop it before replacing it.
    await this.teardown();

    try {
      const client = await braintreeClient.create({ authorization });
      this.paypalCheckout = (await braintreePaypalCheckoutV6.create({ client })) as unknown as PayPalCheckoutV6Instance;
      // Load the PayPal Web SDK v6 so a payment session can be started on the buyer's click.
      await this.paypalCheckout.loadPayPalSDK();

      this.initializedFor = authorization;
      return true;
    } catch (error) {
      this.logService.error('Braintree initialize failed:', error);
      // Never leave a half-built instance behind for the next initialize() to reuse.
      this.paypalCheckout = null;
      this.initializedFor = null;
      return false;
    }
  }

  /**
   * Release the PayPal Checkout V6 instance and the SDK resources it registered. Safe to call when
   * nothing is set up. Called from the provider component's ngOnDestroy and before re-initialising
   * with a different client token.
   */
  async teardown(): Promise<void> {
    const checkout = this.paypalCheckout;
    this.paypalCheckout = null;
    this.initializedFor = null;

    if (!checkout) {
      return;
    }

    try {
      await checkout.teardown();
    } catch (error) {
      // A failed teardown must not block re-initialising or unmounting the component.
      this.logService.error('Braintree teardown failed:', error);
    }
  }

  /**
   * Launch the PayPal V6 payment flow for the given context. Must be called after initialize()
   * resolves true, and from a user gesture (V6 `session.start()` needs transient activation). Chooses
   * the vault or checkout session by `context.mode`, wires onApprove → tokenizePayment → onAuthorised,
   * and surfaces cancel/error through the supplied callbacks.
   */
  async startCheckout(context: ExpressCheckoutContext, options: BraintreePaypalSessionOptions): Promise<boolean> {
    const checkout = this.paypalCheckout;
    if (!checkout) {
      this.logService.error('Braintree: PayPal V6 SDK not ready, cannot start checkout');
      return false;
    }

    const callbacks: PayPalV6ApprovalCallbacks = {
      onApprove: (data: unknown) =>
        checkout.tokenizePayment(data).then((payload) => {
          options.onAuthorised(payload.nonce);
          return payload;
        }),
      onCancel: (data: unknown) => options.onCancelled?.(data),
      onError: (error: unknown) => options.onError?.(error),
    };

    try {
      // card-token → save a payment method (vault; no amount/currency — a bare billing agreement).
      // marketplace → pay a basket now (one-time checkout; amount/currency required).
      const session =
        context.mode === 'card-token'
          ? checkout.createBillingAgreementSession(callbacks)
          : checkout.createOneTimePaymentSession({
              ...callbacks,
              amount: (context.amount / 100).toFixed(2),
              currency: context.currencyCode,
            });
      await session.start();
      return true;
    } catch (error) {
      this.logService.error('Braintree PayPal V6 start failed:', error);
      options.onError?.(error);
      return false;
    }
  }

  /**
   * Complete the express checkout with the PayPal nonce. Routes to the card-token or marketplace
   * complete endpoint by `context.mode` via `completeExpress`. Resolves to true on success.
   */
  async submitNonce(nonce: string, context: ExpressCheckoutContext): Promise<boolean> {
    try {
      const callback: ExpressCheckoutCallback = { responseString: nonce };
      const response: any = await this.expressCheckoutService.completeExpress(context, ProviderName.PayPal, callback);
      return !!(response?.Success ?? response?.success);
    } catch (error) {
      this.logService.error('Braintree submitNonce failed:', error);
      return false;
    }
  }
}
